/**
 * Mon-Sun week bucketing (ADR-0006 "Week semantics"). A week is identified
 * by its Monday date (`WeekStart`); `weekOf(date)` is the Monday on or
 * before `date`. Pure string/date-math, no timezone library needed
 * (ADR-0006 consequence: "date math is string-safe").
 *
 * This is the one place habit weekly-counting derives its window from --
 * `projection.ts` and `logging.ts` both call `weekOf`/`weekSunday` rather
 * than recomputing day-of-week arithmetic themselves.
 */

import type { IsoDate, WeekStart } from '../../contracts';

/** Zero-indexed day-of-week for an ISO date string, Mon=0..Sun=6. */
function isoWeekday(date: string): number {
  const [y, m, d] = date.split('-').map(Number);
  // Sun=0..Sat=6 from getUTCDay(); rotate so Mon=0..Sun=6 (ADR-0006 weeks).
  const jsDay = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1)).getUTCDay();
  return (jsDay + 6) % 7;
}

export function addDaysToDate(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const ms = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1) + days * 24 * 60 * 60 * 1000;
  const result = new Date(ms);
  const year = result.getUTCFullYear().toString().padStart(4, '0');
  const month = (result.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = result.getUTCDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** The Monday on or before `date` (ADR-0006: "weekOf(date)"). */
export function weekOf(date: string): WeekStart {
  const weekday = isoWeekday(date);
  return addDaysToDate(date, -weekday) as WeekStart;
}

/** The Sunday closing out the week that starts on `weekStart`. */
export function weekSunday(weekStart: WeekStart): IsoDate {
  return addDaysToDate(weekStart, 6) as IsoDate;
}

/** True when `date` falls within the Mon-Sun week identified by `weekStart`. */
export function isInWeek(date: string, weekStart: WeekStart): boolean {
  const sunday = weekSunday(weekStart);
  return date >= weekStart && date <= sunday;
}
